import React, { useContext, useEffect, useState } from "react";

import {
  Alert,
  Button,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from "@mui/material";

import { Action } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import { Group } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/groups_pb";

import { regionValuesArr } from "../../../api/index";
import { DEFAULT_REGION_CODE } from "../../../constants/index";
import {
  groupDifference,
  groupDiscreteNot,
  groupIntersection,
  groupUnion,
} from "../../../helpers/app-settings-helpers";
import { ActionByRegion, GroupByRegion } from "../../../types/permissions";
import permissionStyles from "../styles";
import {
  addPermissions as addPermissionsReq,
  listGroups,
  listGroupsByAction,
  removePermissions as removePermissionsReq,
} from "api/endpoints/periscope-admin";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";

type TransferListByGroupProps = {
  actionsByRegion: ActionByRegion;
  selectedAction: Action;
  isActionsLoading: boolean;
};

const TransferListByGroup: React.FC<TransferListByGroupProps> = ({
  actionsByRegion,
  selectedAction,
  isActionsLoading,
}) => {
  const classes = permissionStyles();
  const { showError } = useContext(SnackbarContext);
  const [openPermissionSnackBar, setOpenPermissionSnackBar] = useState(false);
  const [allGroupsList, setAllGroupsList] = useState<Group[]>([]);
  const [checked, setChecked] = useState<Group[]>([]);
  const [unAssignedGroups, setUnAssignedGroups] = useState<Group[]>([]);
  const [assignedGroups, setAssignedGroups] = useState<Group[]>([]);
  const [groupsByRegion, setGroupsByRegion] = useState<GroupByRegion>({
    us: {
      groupsList: [],
    },
    eu: {
      groupsList: [],
    },
    au: {
      groupsList: [],
    },
  });
  const [isLoadingGroupsData, setIsLoadingGroupsData] = useState(false);
  const [isLoadingGroupsAssigned, setIsLoadingGroupsAssigned] = useState(false);
  const [isUpdatingPermissions, setIsUpdatingPermissions] = useState(false);
  const defaultRegion =
    regionValuesArr.length === 1 ? regionValuesArr[0] : DEFAULT_REGION_CODE;
  const unasssignedGroupsChecked = groupIntersection(checked, unAssignedGroups);
  const assignedGroupsChecked = groupIntersection(checked, assignedGroups);

  const handlePermissionSnackBarClose = () => {
    setOpenPermissionSnackBar(false);
  };

  const getAllGroups = async () => {
    setIsLoadingGroupsData(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return listGroups(callOptionsHeaders(regionCode))
          .then((groupsListResp) => {
            if (regionCode === defaultRegion) {
              setAllGroupsList([...groupsListResp]);
            }
            const updatedGroupsByRegion = { ...groupsByRegion };
            updatedGroupsByRegion[regionCode].groupsList = [...groupsListResp];
            setGroupsByRegion(updatedGroupsByRegion);
          })
          .catch(showError);
      }),
    );
    setIsLoadingGroupsData(false);
  };

  const getAssignedGroups = async () => {
    setIsLoadingGroupsAssigned(true);
    listGroupsByAction(
      {
        actionType: selectedAction.actionType,
      },
      callOptionsHeaders(DEFAULT_REGION_CODE),
    )
      .then((groupsListResp) => {
        setAssignedGroups([...groupsListResp]);
      })
      .finally(() => {
        setIsLoadingGroupsAssigned(false);
      });
  };

  const getMatchingActionByRegion = (regionCode: string, action: Action) => {
    const matchingAction = actionsByRegion[regionCode].actionsList.find(
      (actionByRegion) => actionByRegion.actionType === action.actionType,
    );
    return matchingAction ?? action;
  };

  const getGroupIdByRegion = (regionCode: string, group: Group) => {
    const targetGroup = groupsByRegion[regionCode].groupsList.find(
      (groupByRegion) => groupByRegion.name === group.name,
    );

    if (targetGroup) {
      return targetGroup.id;
    }

    return group.id;
  };

  const addPermissions = async (groups: Group[]) => {
    setIsUpdatingPermissions(true);
    await Promise.all(
      groups.map((group) => addPermissionReq(selectedAction, group)),
    );
    setChecked([]);
    await getAllGroups();
    await getAssignedGroups();
    setIsUpdatingPermissions(false);
  };

  const removePermissions = async (groups: Group[]) => {
    setIsUpdatingPermissions(true);
    await Promise.all(
      groups.map((group) => removePermissionReq(selectedAction, group)),
    );
    setChecked([]);
    await getAllGroups();
    await getAssignedGroups();
    setIsUpdatingPermissions(false);
  };

  const addPermissionReq = async (action: Action, group: Group) => {
    // With the way the backend is implemented, there is a permissions DB in each region
    // Need to make a call for each region
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return addPermissionsReq(
          {
            groupId: getGroupIdByRegion(regionCode, group),
            actionsList: [getMatchingActionByRegion(regionCode, action)],
          },
          callOptionsHeaders(regionCode),
        ).catch(showError);
      }),
    );
  };

  const removePermissionReq = async (action: Action, group: Group) => {
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return removePermissionsReq(
          {
            groupId: getGroupIdByRegion(regionCode, group),
            actionsList: [getMatchingActionByRegion(regionCode, action)],
          },
          callOptionsHeaders(regionCode),
        ).catch(showError);
      }),
    );
  };

  const numberOfChecked = (groups: Group[]) => {
    return groupIntersection(checked, groups).length;
  };

  const handleToggle = (group: Group) => () => {
    const newChecked = [...checked];
    const currentIndex = checked.indexOf(group);
    // if its not in the checked array add it to the checked array
    if (currentIndex === -1) {
      newChecked.push(group);
      // if its already in the checked array, at currentIndex position,
      // remove 1 item from the checked array
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleToggleAll = (groups: Group[]) => {
    if (numberOfChecked(groups) === groups.length) {
      setChecked(groupDiscreteNot(checked, groups));
    } else {
      setChecked(groupUnion(checked, groups));
    }
  };

  useEffect(() => {
    getAllGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getAssignedGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAction]);

  useEffect(() => {
    setUnAssignedGroups(groupDifference(allGroupsList, assignedGroups));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedGroups, allGroupsList, selectedAction]);

  const customList = (title: React.ReactNode, groups: Group[]) => {
    return (
      <Card sx={classes.card}>
        <CardHeader
          sx={classes.cardHeader}
          avatar={
            <Checkbox
              onClick={() => handleToggleAll(groups)}
              checked={
                numberOfChecked(groups) === groups.length && groups.length !== 0
              }
              indeterminate={
                numberOfChecked(groups) !== groups.length &&
                numberOfChecked(groups) !== 0
              }
              disabled={groups.length === 0}
              inputProps={{ "aria-label": "all groups selected" }}
            />
          }
          title={title}
          titleTypographyProps={{ variant: "body1" }}
          subheader={`${numberOfChecked(groups)}/${groups.length} selected`}
        />
        <Divider />
        {isActionsLoading ||
        isLoadingGroupsData ||
        isUpdatingPermissions ||
        isLoadingGroupsAssigned ? (
          <CircularProgress
            color="primary"
            sx={classes.loadingSpinner}
            thickness={1}
            size={90}
          />
        ) : (
          <List sx={classes.list} dense component="div" role="list">
            {groups.map((group: Group, index) => {
              const labelId = `transfer-list-all-item-${group.id}-label`;
              return (
                <ListItem key={`transfer-list-all-item-${group.id}-${index}`}>
                  <ListItemButton role="listitem" onClick={handleToggle(group)}>
                    <ListItemIcon>
                      <Checkbox
                        checked={checked.indexOf(group) !== -1}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ "aria-labelledby": labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={group.name} />
                  </ListItemButton>
                </ListItem>
              );
            })}
            <ListItem />
          </List>
        )}
      </Card>
    );
  };

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      alignItems="center"
      sx={classes.root}
    >
      <Grid item>{customList("Unassigned Groups", unAssignedGroups)}</Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            variant="contained"
            size="large"
            color="primary"
            sx={classes.button}
            onClick={() => addPermissions(unasssignedGroupsChecked)}
            disabled={unasssignedGroupsChecked.length === 0}
          >
            &gt;
          </Button>
          <Button
            variant="contained"
            size="large"
            color="primary"
            sx={classes.button}
            onClick={() => removePermissions(assignedGroupsChecked)}
            disabled={assignedGroupsChecked.length === 0}
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item>{customList("Assigned Groups", assignedGroups)}</Grid>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openPermissionSnackBar}
        onClose={handlePermissionSnackBarClose}
        autoHideDuration={3500}
      >
        <Alert severity="success" variant="filled">
          Permissions successfully allocated to groups
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default TransferListByGroup;
